import { useEffect, useRef } from "react";

/** Two signals closer together than this are one return to the tab. */
const COALESCE_MS = 1000;

/**
 * Re-fetches when the person comes back to the tab.
 *
 * Screens here poll while something is visibly in flight — a file waiting on a malware
 * scan, say — and then give up, because a tab left open overnight should not sit asking
 * the server a question every ten seconds until the laptop's battery runs out.
 *
 * That leaves a real gap. Somebody uploads a file, switches to Outlook to find the error
 * message they were asked for, comes back a few minutes later, and the screen is still
 * showing what it knew when they left. The state changed while nobody was looking, and
 * the only way to see it was a manual reload — which people reasonably read as the
 * feature being broken.
 *
 * Coming back to the tab is the cheapest possible signal that somebody is about to look
 * at this data again, so that is when we re-ask. No timer, no cost while the tab is
 * hidden, and it covers the case polling deliberately abandons.
 *
 * Both events are needed and neither is redundant: `visibilitychange` fires when a
 * background tab is brought forward, `focus` when the window itself regains focus with
 * the tab already frontmost — alt-tabbing back from another application.
 */
export function useRefreshOnFocus(refresh: () => void, enabled = true) {
  // Held in a ref so a caller passing an inline arrow does not tear down and rebuild the
  // listeners on every render.
  const latest = useRef(refresh);
  latest.current = refresh;

  // ⚠️ Both events fire for ONE return to the tab — bringing a background tab forward gives
  // the window focus as well — so without this every screen using the hook asked its question
  // twice, and the session provider three times once the response's own permission stamp
  // joined in. One return is one refresh; a second signal inside a second is the same return.
  const lastFiredAt = useRef(0);

  useEffect(() => {
    if (!enabled) return;

    function onVisible() {
      if (document.visibilityState !== "visible") return;

      const now = Date.now();
      if (now - lastFiredAt.current < COALESCE_MS) return;
      lastFiredAt.current = now;

      latest.current();
    }

    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);

    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
    };
  }, [enabled]);
}
